import {useCallback, useEffect, useState} from "react";
import playgroundApi from "../../../services/playgroundApi";
import {
  MusicHomeCard,
  MusicHomeLessonCard,
  MusicHomePlaylistCard,
  MusicHomeResponse,
  MusicHomeSongCard
} from "../../../client/models/musicHome/MusicHomeResponse";
import {useLocation, useNavigate} from "react-router-dom";
import {useAppSelector} from "../../../redux/hooks";
import {filterMusicHomeResponse} from "./SongsResultsHomeFiltering.logic";
import {mySongsPageStore} from "../../../pages/songs-router/modules/my-songs-page-store/mySongsPage.store";
import {
  useLibraryLessonCardNavigationLogic
} from "components/cards/library-lesson-card/LibraryLessonCardNavigation.logic";
import {getSongCardUrl} from "../../../components/common/song-card/SongCard";
import {getPlaylistCardUrl} from "../../../components/cards/playlistCard/PlaylistCard";
import {IFeaturedCardProps} from "../../../components/cards/featuredCard/FeaturedCard";
import {ILibraryLessonCardProps} from "../../../components/cards/library-lesson-card/LibraryLessonCard";
import {musicHomeStore} from "../../../pages/songs-router/modules/music-home-store/musicHomeStore";

export interface ISongResultsHomeLogic {
  isLoading: boolean;
  musicHomeResponse: MusicHomeResponse;
  selectedGroupId: number;
  seeAll(groupId: number): void;
  backToMusicHome(): void;
  handleSwitchSearchEngines: () => void;
  calculateFeaturedCardProps(card: MusicHomeCard): IFeaturedCardProps;
  calculateLessonCardProps(card: MusicHomeLessonCard): ILibraryLessonCardProps;
}

function calculateFeaturedCardUrl(card: MusicHomePlaylistCard | MusicHomeSongCard): string {
  switch (card.type) {
    case "song":
      return getSongCardUrl(card.songId);
    case "playlist":
      return getPlaylistCardUrl(card.playlistId);
  }
}

function calculateSubtext(card: MusicHomeCard): string {
  switch (card.type) {
    case "song":
      return `${card.numberOfArrangements} arrangements`;
    case "lesson":
      return card.levelDifficulty;
    default:
      return card.subtext;
  }
}

export const useSongResultsHomeLogic = (): ISongResultsHomeLogic => {
  const [isLoading, setIsLoading] = useState(true);
  const [musicHomeResponse, setMusicHomeResponse] = useState<MusicHomeResponse>({groups: []});
  const navigate = useNavigate();
  const location = useLocation();
  const searchTerm = useAppSelector(musicHomeStore.getSearchText);
  const {onArrangementClick, isCheckingWhetherLessonIsAccessible} = useLibraryLessonCardNavigationLogic();

  useEffect(() => {
    playgroundApi.musicHome()
      .then((response) => {
        if (response.groups.length >= 1) {
          response.groups[0].isFeatured = true;
        }

        setMusicHomeResponse(response);
      }).finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleSwitchSearchEngines = useCallback(() => {
    mySongsPageStore.setSearchValue(searchTerm);
    navigate('/songs/browse');
  }, [searchTerm, navigate]);

  const filteredMusicHomeResponse = filterMusicHomeResponse(searchTerm, musicHomeResponse);

  function calculateFeaturedCardProps(card: MusicHomeCard): IFeaturedCardProps {
    const difficultyLevelFromApi = card.type !== "song" ? card.difficultyIdentifier : undefined;
    const onClick = card.type === "lesson" ? onArrangementClick(card.lessonId) : () => navigate(calculateFeaturedCardUrl(card));
    return {
      headline: card.headline,
      title: card.title,
      artistName: card.type === "playlist" ? '' : card.artistName,
      hasVideo: card.type === "playlist" ? false : card.hasVideo,
      difficultyOrCount: calculateSubtext(card),
      difficultyLevel: difficultyLevelFromApi !== "mixed" ? difficultyLevelFromApi : undefined,
      imgUrl: card.imageUrl,
      onClick,
    };
  }

  function calculateLessonCardProps(card: MusicHomeLessonCard): ILibraryLessonCardProps {
    return {
      title: card.title,
      lessonId: card.lessonId,
      difficultyIdentifier: card.difficultyIdentifier,
      difficultyName: card.levelDifficulty,
      imageUrl: card.imageUrl,
      icon: card.hasVideo ? "video" : "none",
      subTitle: card.artistName,
      onClick: onArrangementClick(card.lessonId),
    };
  }

  return {
    isLoading: isLoading || isCheckingWhetherLessonIsAccessible,
    musicHomeResponse: filteredMusicHomeResponse,
    selectedGroupId: location.state?.groupId ?? 0,
    seeAll: (groupId: number) => {
      navigate('.', {state: {groupId: groupId}});
    },
    backToMusicHome() {
      navigate('.', {state: {groupId: 0}});
    },
    handleSwitchSearchEngines,
    calculateFeaturedCardProps,
    calculateLessonCardProps,
  };
}
